import { FormResponseValue, PreviousFormCompoundLogic } from "@tellescope/types-models";
import { evaluateResponseEquals } from "./evaluate-response-equals";
import { responsesSatisfyConditions } from "./responses-satisfy-conditions";
import { FormFieldNode, NextFieldLogicOptions } from "./types";

/**
 * Determines the next field based on the current field, response, and conditional logic
 * Handles:
 * - Compound logic with priority sorting
 * - Previous field equality conditions
 * - Default "after" logic
 */
export const getNextField = (
  activeField: FormFieldNode,
  currentValue: FormResponseValue | undefined,
  responses: FormResponseValue[],
  options?: NextFieldLogicOptions
): FormFieldNode | undefined => {
  if (activeField.children.length === 0) {
    return undefined;
  }

  try {
    const advancedLogic = activeField.children
      .filter(
        (c) =>
          !!c.value.previousFields.find((p) => p.type === "compoundLogic" && p.info.fieldId === currentValue?.fieldId)
      )
      .map((c) => ({
        node: c,
        logic: c.value.previousFields.find(
          (p) => p.type === "compoundLogic" && p.info.fieldId === currentValue?.fieldId
        ) as PreviousFormCompoundLogic,
      }))
      .sort((v1, v2) => v2.logic.info.priority - v1.logic.info.priority); // sort descending (highest priority at index 0)

    for (const { node, logic } of advancedLogic) {
      if (responsesSatisfyConditions(responses, logic.info.condition, options)) {
        return node;
      }
    }
  } catch (err) {
    console.error(err);
  }

  return (
    activeField.children.find((c) =>
      c.value.previousFields.find(
        (p) =>
          p.type === "previousEquals" &&
          p.info.fieldId === currentValue?.fieldId &&
          evaluateResponseEquals(currentValue.answer, p.info.equals)
      )
    ) ||
    activeField.children.find((c) =>
      c.value.previousFields.find((p) => p.type === "after" && p.info.fieldId === activeField.value.id)
    )!
  );
};
